import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import {
  Archive,
  Ban,
  BadgeDollarSign,
  CheckCircle2,
  FileText,
  MessageSquarePlus,
  MoreHorizontal,
  Package,
  PauseCircle,
  Pencil,
  PlayCircle,
  RotateCcw,
  Send,
  Truck,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormLayout } from '@nucleus-commerce/admin-kit/forms';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Can, usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { OrderDocumentKind, OrderLine } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import { Tooltip, TooltipContent, TooltipTrigger } from '@nucleus-commerce/ui/components/tooltip';

import {
  OrderLabelChips,
  OrderProductionCard,
  orderWorkflowQueryOptions,
} from '@/features/production';
import { OrderReturnsCard } from '@/features/returns';
import { useStore } from '@/shared/store/store-context';

import { AddNoteDialog } from '../components/add-note-dialog';
import { CancelOrderDialog } from '../components/cancel-order-dialog';
import { EditAddressDialog } from '../components/edit-address-dialog';
import { EditContactDialog } from '../components/edit-contact-dialog';
import { EditLineDialog } from '../components/edit-line-dialog';
import { FulfillDialog } from '../components/fulfill-dialog';
import { HoldDialog } from '../components/hold-dialog';
import { MarkPaidDialog } from '../components/mark-paid-dialog';
import { OrderAddressCard } from '../components/order-address-card';
import { OrderCustomerCard } from '../components/order-customer-card';
import { OrderDocumentDialog } from '../components/order-document-dialog';
import { OrderFulfillmentsCard } from '../components/order-fulfillments-card';
import { OrderLinesTable } from '../components/order-lines-table';
import { OrderNotesCard } from '../components/order-notes-card';
import { OrderNotificationsCard } from '../components/order-notifications-card';
import { OrderPaymentCard } from '../components/order-payment-card';
import { OrderRefundsCard } from '../components/order-refunds-card';
import {
  ApprovalStatusBadge,
  AuctionOrderBadge,
  FinancialStatusBadge,
  FulfillmentStatusBadge,
  OrderStatusBadge,
} from '../components/order-status-badges';
import { OrderTimeline } from '../components/order-timeline';
import { OrderTotalsCard } from '../components/order-totals-card';
import { RecordApprovalDialog } from '../components/record-approval-dialog';
import { RefundDialog } from '../components/refund-dialog';
import { SendDesignDialog } from '../components/send-design-dialog';
import { useOrder } from '../hooks/use-order';
import { useArchiveOrder, useReleaseOrder } from '../hooks/use-order-mutations';
import { canEditLine, canRecordApproval } from '../schemas/order-edit-schemas';
import {
  canArchive,
  canEditAddress,
  canHold,
  canRefund,
  canSendDesign,
  cancelBlocker,
  fulfilBlocker,
  isApprovalPending,
  isAwaitingPayment,
  workflowControl,
} from '../schemas/order-rules';

const ROUTE = '/_authed/_active/s/$storeId/orders/$orderId' as const;

type Dialog =
  | { kind: 'markPaid' | 'cancel' | 'note' | 'address' | 'contact' | 'fulfil' | 'refund' }
  | { kind: 'hold' | 'design' | 'approval' | 'forceRelease' }
  | { kind: 'line'; line: OrderLine }
  | { kind: 'document'; document: OrderDocumentKind }
  | null;

function BlockedButton({
  label,
  blocked,
  icon,
  onClick,
  variant = 'outline',
  tour,
}: {
  label: string;
  blocked: string | null;
  icon: React.ReactNode;
  onClick: () => void;
  variant?: 'outline' | 'default';
  tour?: string;
}) {
  const button = (
    <Button
      variant={variant}
      disabled={Boolean(blocked)}
      onClick={onClick}
      data-tour={blocked ? undefined : tour}
    >
      {icon}
      {label}
    </Button>
  );
  if (!blocked) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex" data-tour={tour}>
          {button}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{blocked}</TooltipContent>
    </Tooltip>
  );
}

function OrderDetail() {
  const { t } = useTranslation('orders');
  const format = useFormat();
  const store = useStore();
  const { can } = usePermissions();
  const message = useErrorMessage();
  const { orderId } = useParams({ from: ROUTE });
  // Prefetched by the route loader, so this resolves from cache.
  const { data: order } = useOrder(store.id, orderId);
  const workflowQuery = useQuery({
    ...orderWorkflowQueryOptions(store.id, orderId),
    enabled: Boolean(order?.workflow),
  });
  const release = useReleaseOrder(store.id);
  const archive = useArchiveOrder(store.id);
  const [dialog, setDialog] = useState<Dialog>(null);
  if (!order) return null;

  const close = (open: boolean) => {
    if (!open) setDialog(null);
  };
  const open = (kind: Exclude<NonNullable<Dialog>, { kind: 'line' | 'document' }>['kind']) =>
    setDialog({ kind });
  const addressEditable = can('orders.edit') && canEditAddress(order);
  const blocker = cancelBlocker(order);
  // A paid order can be cancelled with its refund in one step by a member who may refund.
  const cancelBlock = blocker === 'paid' && can('orders.refund') ? null : blocker;
  const fulfilBlock = fulfilBlocker(order);
  const onHold = order.fulfillmentStatus === 'on_hold';
  const owns = workflowControl(order, workflowQuery.data);
  const designSendable = !owns.approval && canSendDesign(order);
  const approvalRecordable = canRecordApproval(order);
  const openToFulfil = fulfilBlock !== 'cancelled' && fulfilBlock !== 'nothing_open';
  const showFulfil = !owns.fulfilment && openToFulfil;
  const workflowHint = (owns.approval && canSendDesign(order)) || (owns.fulfilment && openToFulfil);
  const archivable = can('orders.write') && canArchive(order);

  const releaseHold = (force = false) =>
    release.mutate(
      { id: order.id, force },
      {
        onError: (error) => {
          if (!force && isApprovalPending(error) && can('orders.edit')) {
            open('forceRelease');
            return;
          }
          toast.error(message(error));
        },
      },
    );

  return (
    <PageContent>
      <PageHeader
        title={<span className="font-mono">{order.name}</span>}
        meta={
          <span
            className="flex flex-wrap items-center gap-2"
            data-tour="orders.detail.status-badges"
          >
            <OrderStatusBadge status={order.status} />
            <FinancialStatusBadge status={order.financialStatus} />
            <FulfillmentStatusBadge status={order.fulfillmentStatus} />
            {order.approvalStatus ? <ApprovalStatusBadge status={order.approvalStatus} /> : null}
            {order.auction ? <AuctionOrderBadge /> : null}
            <OrderLabelChips labels={order.workflow?.labels ?? []} />
          </span>
        }
        description={
          <span title={format.utc(order.placedAt)}>
            {order.email
              ? t('detail.placedBy', { when: format.relative(order.placedAt), email: order.email })
              : t('detail.placedWalkIn', { when: format.relative(order.placedAt) })}
            {order.cancelledAt
              ? ` · ${t('detail.cancelledAt', { when: format.relative(order.cancelledAt) })}`
              : null}
          </span>
        }
        backTo={{ to: '/s/$storeId/orders', params: { storeId: store.id } }}
        backLabel={t('detail.back')}
        actions={
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  aria-label={t('actions.more')}
                  data-tour="orders.detail.more-menu"
                >
                  <MoreHorizontal aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => setDialog({ kind: 'document', document: 'packing-slip' })}
                >
                  <Package aria-hidden />
                  {t('actions.packingSlip')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => setDialog({ kind: 'document', document: 'invoice' })}
                >
                  <FileText aria-hidden />
                  {t('actions.invoice')}
                </DropdownMenuItem>
                {can('orders.edit') && order.status !== 'archived' ? (
                  <DropdownMenuItem onSelect={() => open('contact')}>
                    <Pencil aria-hidden />
                    {t('actions.editContact')}
                  </DropdownMenuItem>
                ) : null}
                {can('orders.write') &&
                (designSendable || approvalRecordable || onHold || canHold(order)) ? (
                  <DropdownMenuSeparator />
                ) : null}
                {can('orders.write') && designSendable ? (
                  <DropdownMenuItem
                    onSelect={() => open('design')}
                    data-tour="orders.detail.send-design"
                  >
                    <Send aria-hidden />
                    {t('actions.sendDesign')}
                  </DropdownMenuItem>
                ) : null}
                {can('orders.write') && approvalRecordable ? (
                  <DropdownMenuItem onSelect={() => open('approval')}>
                    <CheckCircle2 aria-hidden />
                    {t('actions.recordApproval')}
                  </DropdownMenuItem>
                ) : null}
                {can('orders.write') && onHold ? (
                  <DropdownMenuItem disabled={release.isPending} onSelect={() => releaseHold()}>
                    <PlayCircle aria-hidden />
                    {t('actions.release')}
                  </DropdownMenuItem>
                ) : can('orders.write') && canHold(order) ? (
                  <DropdownMenuItem onSelect={() => open('hold')}>
                    <PauseCircle aria-hidden />
                    {t('actions.hold')}
                  </DropdownMenuItem>
                ) : null}
                {archivable ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      disabled={archive.isPending}
                      onSelect={() =>
                        archive.mutate(
                          { id: order.id },
                          { onError: (error) => toast.error(message(error)) },
                        )
                      }
                    >
                      <Archive aria-hidden />
                      {t('actions.archive')}
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
            <Can permission="orders.write">
              <Button variant="outline" onClick={() => open('note')}>
                <MessageSquarePlus aria-hidden />
                {t('actions.addNote')}
              </Button>
            </Can>
            {blocker !== 'cancelled' ? (
              <Can permission="orders.cancel">
                <BlockedButton
                  label={t('actions.cancel')}
                  blocked={cancelBlock ? t(`cancel.blocked.${cancelBlock}`) : null}
                  icon={<Ban aria-hidden />}
                  onClick={() => open('cancel')}
                  tour="orders.detail.cancel"
                />
              </Can>
            ) : null}
            {canRefund(order) ? (
              <Can permission="orders.refund">
                <Button
                  variant="outline"
                  onClick={() => open('refund')}
                  data-tour="orders.detail.refund"
                >
                  <RotateCcw aria-hidden />
                  {t('actions.refund')}
                </Button>
              </Can>
            ) : null}
            {workflowHint ? (
              <Can permission="orders.write">
                <span className="text-muted-foreground self-center text-xs">
                  {t('detail.workflowOwns')}
                </span>
              </Can>
            ) : null}
            {showFulfil ? (
              <Can permission="fulfillments.write">
                <BlockedButton
                  label={t('actions.fulfil')}
                  blocked={fulfilBlock ? t(`fulfil.blocked.${fulfilBlock}`) : null}
                  icon={<Truck aria-hidden />}
                  onClick={() => open('fulfil')}
                  variant={isAwaitingPayment(order) ? 'outline' : 'default'}
                  tour="orders.detail.fulfil"
                />
              </Can>
            ) : null}
            {isAwaitingPayment(order) ? (
              <Can permission="orders.write">
                <Button onClick={() => open('markPaid')} data-tour="orders.detail.mark-paid">
                  <BadgeDollarSign aria-hidden />
                  {t('actions.markPaid')}
                </Button>
              </Can>
            ) : null}
          </>
        }
      />
      <FormLayout
        aside={
          <>
            <div className="grid grid-cols-1" data-tour="orders.detail.customer">
              <OrderCustomerCard order={order} />
            </div>
            <OrderAddressCard
              title={t('address.shipping')}
              address={order.shippingAddress}
              onEdit={addressEditable ? () => open('address') : undefined}
              lockedHint={
                order.shippingAddress && !canEditAddress(order) ? t('address.locked') : undefined
              }
            />
            <OrderAddressCard title={t('address.billing')} address={order.billingAddress} />
            <div className="grid grid-cols-1" data-tour="orders.detail.notes">
              <OrderNotesCard storeId={store.id} order={order} readOnly={!can('orders.edit')} />
            </div>
            <OrderNotificationsCard
              storeId={store.id}
              order={order}
              canResend={can('orders.write')}
            />
          </>
        }
      >
        <div className="grid grid-cols-1" data-tour="orders.detail.lines">
          <OrderLinesTable
            lines={order.lines}
            onEditLine={
              can('orders.edit') ? (line) => setDialog({ kind: 'line', line }) : undefined
            }
            canEditLine={(line) => canEditLine(order, line)}
          />
        </div>
        {order.workflow || (order.financialStatus === 'paid' && order.status !== 'cancelled') ? (
          <OrderProductionCard storeId={store.id} order={order} />
        ) : null}
        {order.note ? (
          <div className="bg-muted/40 rounded-lg border p-4 text-sm">
            <p className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
              {t('detail.customerNote')}
            </p>
            <p className="whitespace-pre-line">{order.note}</p>
          </div>
        ) : null}
        <div className="grid gap-6 md:grid-cols-2">
          <div className="grid grid-cols-1" data-tour="orders.detail.totals">
            <OrderTotalsCard order={order} />
          </div>
          <OrderPaymentCard payments={order.payments} auction={order.auction} storeId={store.id} />
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="grid grid-cols-1" data-tour="orders.detail.fulfilments">
            <OrderFulfillmentsCard
              storeId={store.id}
              order={order}
              canWrite={can('fulfillments.write')}
            />
          </div>
          {order.refunds.length > 0 ? <OrderRefundsCard order={order} /> : null}
          {order.returns.length > 0 || order.lines.some((line) => line.fulfilledQuantity > 0) ? (
            <div className="grid grid-cols-1" data-tour="orders.detail.open-return">
              <OrderReturnsCard storeId={store.id} order={order} />
            </div>
          ) : null}
        </div>
        <div className="grid grid-cols-1" data-tour="orders.detail.timeline">
          <OrderTimeline events={order.events} />
        </div>
      </FormLayout>
      {dialog?.kind === 'markPaid' ? (
        <MarkPaidDialog storeId={store.id} order={order} open onOpenChange={close} />
      ) : null}
      {dialog?.kind === 'cancel' ? (
        <CancelOrderDialog storeId={store.id} order={order} open onOpenChange={close} />
      ) : null}
      {dialog?.kind === 'note' ? (
        <AddNoteDialog storeId={store.id} order={order} open onOpenChange={close} />
      ) : null}
      {dialog?.kind === 'address' ? (
        <EditAddressDialog storeId={store.id} order={order} open onOpenChange={close} />
      ) : null}
      {dialog?.kind === 'contact' ? (
        <EditContactDialog storeId={store.id} order={order} onOpenChange={close} />
      ) : null}
      {dialog?.kind === 'line' ? (
        <EditLineDialog storeId={store.id} order={order} line={dialog.line} onOpenChange={close} />
      ) : null}
      {dialog?.kind === 'fulfil' ? (
        <FulfillDialog storeId={store.id} order={order} open onOpenChange={close} />
      ) : null}
      {dialog?.kind === 'refund' ? (
        <RefundDialog storeId={store.id} order={order} open onOpenChange={close} />
      ) : null}
      {dialog?.kind === 'hold' ? (
        <HoldDialog storeId={store.id} order={order} open onOpenChange={close} />
      ) : null}
      {dialog?.kind === 'design' ? (
        <SendDesignDialog storeId={store.id} order={order} open onOpenChange={close} />
      ) : null}
      {dialog?.kind === 'approval' ? (
        <RecordApprovalDialog storeId={store.id} order={order} onOpenChange={close} />
      ) : null}
      {dialog?.kind === 'document' ? (
        <OrderDocumentDialog
          storeId={store.id}
          orderId={order.id}
          kind={dialog.document}
          onOpenChange={close}
        />
      ) : null}
      <AlertDialog open={dialog?.kind === 'forceRelease'} onOpenChange={close}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('forceRelease.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('forceRelease.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('forceRelease.keep')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setDialog(null);
                releaseHold(true);
              }}
            >
              {t('forceRelease.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContent>
  );
}

export function OrderDetailPage() {
  return (
    <FeatureErrorBoundary name="orders.detail">
      <OrderDetail />
    </FeatureErrorBoundary>
  );
}
